import { format } from "date-fns";
import { RRule } from "rrule";
import * as ICalUtils from "./ICalUtils.js";

// -------------------- CONFIG --------------------
export const MAX_SLOTS_PER_HOUR = 5; // manual booking only
export const SLOT_MINUTES = 15;

export const TYPE_COLORS = {
  image: "#1976d2", // blue
  video: "#9c27b0", // purple
  web: "#2e7d32",   // green
  pdf: "#ed6c02",   // orange
  other: "#546e7a", // grey
};

// ---------- TYPES / DISPLAY ----------
export function safeType(item) {
  const t = (item?.type || "other").toLowerCase();
  if (t.includes("image")) return "image";
  if (t.includes("video")) return "video";
  if (t.includes("html")) return "html";
  if (t.includes("pdf")) return "pdf";
  return "other";
}

export function formatTooltip(ev) {
  const s = format(ev.start, "PPpp");
  const e = format(ev.end, "PPpp");
  return `${ev.title}\n${s} → ${e}`;
}

export function cleanFilenameLocal(filename) {
  const s = String(filename || "");
  const idx = s.indexOf("_");
  return idx >= 0 ? s.substring(idx + 1) : s;
}

// ----- LIBRARY HELPERS -----
export function cleanFilename(filename) {
  const s = String(filename || "");
  const idx = s.indexOf("_");
  return idx >= 0 ? s.substring(idx + 1) : s;
}

export function detectTypeFromItem(item) {
  const mime = (item?.type || item?.mime_type || item?.content_type || item?.contentType || "").toLowerCase();
  if (mime.includes("image/")) return "image";
  if (mime.includes("video/")) return "video";
  if (mime.includes("text/html")) return "html";
  if (mime.includes("application/pdf")) return "pdf";

  const name = (item?.filename || item?.name || "").toLowerCase();
  const url = (item?.url || "").toLowerCase();
  const s = name || url;

  if (url.includes("tv.tv")) return "stream";
  if (/\.(jpg|jpeg|png|gif|webp|bmp|heic)$/i.test(s)) return "image";
  if (/\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(s)) return "video";
  if (/\.(html|htm)$/i.test(s)) return "html";
  if (/\.(pdf)$/i.test(s)) return "pdf";

  if (url.includes(".jpg") || url.includes(".jpeg") || url.includes(".png") || url.includes(".webp")) return "image";
  if (url.includes(".mp4") || url.includes(".mov") || url.includes(".webm")) return "video";
  if (url.includes(".html") || url.includes(".htm")) return "html";
  if (url.includes(".pdf")) return "pdf";

  return "other";
}

export function mapLibraryResponse(result, { marketplace = false } = {}) {
  let rx = Array.isArray(result?.list) ? [...result.list].reverse() : [];

  //console.log("mapLibraryResponse raw result=", result);
  //console.log("mapLibraryResponse list=", rx);

  rx = rx.filter((item) => {
    if (marketplace && item.content_category === "template") return false;
    const filename = (item.filename || "").toLowerCase();
    if (filename.includes("tv.tv") || filename.includes("virtual_dir")) return false;
    if (item.active === false) return false;
    return true;
  });

  return rx.map((item, idx) => {
    const clean = cleanFilename(item.filename || "");
    const id = String(item.content_id ?? item.id ?? clean ?? idx);
    return {
      id,
      filename: clean,
      name: clean,
      type: detectTypeFromItem(item),
      thumbnail: item.thumbnail || item.url || "https://dummyimage.com/64x64/cccccc/000000.png&text=?",
      _raw: item,
    };
  });
}

export function filterLibrary(library, query) {
  const q = (query || "").trim().toLowerCase();
  const list = Array.isArray(library) ? library : [];
  if (!q) return list;

  const terms = q.split(/\s+/).filter(Boolean);
  return list.filter((x) => {
    const fname = (x?.filename || "").toLowerCase();
    return terms.every((t) => fname.includes(t));
  });
}

// -------------------- ICS PARSING (LOCAL ONLY) --------------------

const VALID_FREQ = "(SECONDLY|MINUTELY|HOURLY|DAILY|WEEKLY|MONTHLY|YEARLY)";

// Extract a VEVENT line like:
// DTSTART:20260219T103000
// DTSTART;TZID=Asia/Singapore:20260219T103000
function _getVeventLine(icsText, key) {
  const s = String(icsText || "").replace(/\r?\n/g, "\n");
  const m = s.match(new RegExp(`^${key}(?:;[^:]*)?:(.+)$`, "mi"));
  return m ? String(m[1] || "").trim() : "";
}

function _parseIcsDateValue(v) {
  // supports:
  // 20260219T103000
  // 20260219T103000Z
  // 20260219T10300000
  // (we only care to minute precision, seconds optional)
  const raw = String(v || "").trim();
  if (!raw) return null;

  const z = /Z$/i.test(raw);
  const core = raw.replace(/Z$/i, "");

  // date only?
  if (/^\d{8}$/.test(core)) {
    const y = +core.slice(0, 4);
    const mo = +core.slice(4, 6);
    const d = +core.slice(6, 8);
    return z ? new Date(Date.UTC(y, mo - 1, d, 0, 0, 0, 0)) : new Date(y, mo - 1, d, 0, 0, 0, 0);
  }

  const m = core.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})?$/);
  if (!m) return null;

  const y = +m[1];
  const mo = +m[2];
  const d = +m[3];
  const hh = +m[4];
  const mm = +m[5];
  const ss = m[6] ? +m[6] : 0;

  return z
    ? new Date(Date.UTC(y, mo - 1, d, hh, mm, ss, 0))
    : new Date(y, mo - 1, d, hh, mm, ss, 0);
}


export const normalizeCrlf = (s) => String(s || "").replace(/\r?\n/g, "\r\n");

export const replaceOrInsertVeventLine = (ics, key, value) => {
  let text = normalizeCrlf(ics);

  const veventMatch = text.match(/BEGIN:VEVENT\r\n([\s\S]*?)\r\nEND:VEVENT/i);
  if (!veventMatch) throw new Error("invalid ics: missing vevent");

  let body = veventMatch[1];

  // remove any old TZID variants of DTSTART/DTEND too
  const keyRe = new RegExp(`^${key}(;[^:]*)?:.*$`, "gmi");

  if (keyRe.test(body)) {
    body = body.replace(keyRe, `${key}:${value}`);
  } else {
    // insert near DTSTART/DTEND area, before RRULE if possible, otherwise end
    if (/^RRULE:/im.test(body)) {
      body = body.replace(/^RRULE:/im, `${key}:${value}\r\nRRULE:`);
    } else {
      body = body + `\r\n${key}:${value}`;
    }
  }

  return text.replace(veventMatch[0], `BEGIN:VEVENT\r\n${body}\r\nEND:VEVENT`);
};

export const updateIcsStartEnd = (ics, dtStartJs, dtEndJs) => {
  const dtstart = ICalUtils.toIcalLocal(new Date(dtStartJs));
  const dtend = ICalUtils.toIcalLocal(new Date(dtEndJs));

  let out = ics;
  out = replaceOrInsertVeventLine(out, "DTSTART", dtstart);
  out = replaceOrInsertVeventLine(out, "DTEND", dtend);

  // also remove any leftover TZID parameters on these lines if present
  out = out.replace(/^DTSTART;TZID=[^:]+:/gmi, "DTSTART:");
  out = out.replace(/^DTEND;TZID=[^:]+:/gmi, "DTEND:");

  return out;
};


export function parseScheduleFromIcs(icsText) {
  if (!String(icsText || "").trim()) return null;

  try {
    // drop RRULE lines with invalid/missing FREQ to avoid rrule.js explosions
    const sanitized = String(icsText).replace(
      new RegExp(`^RRULE:(?!.*\\bFREQ=${VALID_FREQ}\\b).*\\r?\\n`, "gmi"),
      ""
    );

    const dtStartRaw = _getVeventLine(sanitized, "DTSTART");
    const dtEndRaw = _getVeventLine(sanitized, "DTEND");

    const dtStart = _parseIcsDateValue(dtStartRaw);
    const dtEnd = _parseIcsDateValue(dtEndRaw);

    // RRULE (value only)
    let rruleValue = _getVeventLine(sanitized, "RRULE");
    rruleValue = String(rruleValue || "").trim();

    let rule = null;
    if (rruleValue && dtStart) {
      // accept "FREQ=DAILY;..." only
      if (new RegExp(`\\bFREQ=${VALID_FREQ}\\b`, "i").test(rruleValue)) {
        const tmp = RRule.fromString(rruleValue);
        rule = new RRule({ ...tmp.options, dtstart: dtStart });
      }
    }

    return { dtStart, dtEnd, rule };
  } catch (e) {
    console.warn("parseScheduleFromIcs failed:", e, icsText);
    return null;
  }
}

// -------------------- MISC HELPERS --------------------
export function overlaps(aStart, aEnd, bStart, bEnd) {
  return +aStart < +bEnd && +aEnd > +bStart;
}

export async function fetchJsonRobust(url, headers) {
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let text = "";
    try {
      text = await res.text();
    } catch {}
    throw new Error(`http ${res.status} ${res.statusText}${text ? `: ${text}` : ""}`);
  }

  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("application/json")) return res.json();

  const t = await res.text();
  try {
    return JSON.parse(t);
  } catch {
    throw new Error("invalid json response");
  }
}

// -------------------- DEVICE/GROUP SELECT --------------------
export function getGroupIdFromTargetId(targetId) {
  const s = String(targetId || "");
  if (!s.startsWith("_group_")) return -1;
  const n = parseInt(s.substring("_group_".length), 10);
  return Number.isFinite(n) ? n : -1;
}

export function sortDeviceGroupList(a, b) {
  const ag = String(a?.id || "").startsWith("_group_");
  const bg = String(b?.id || "").startsWith("_group_");
  if (ag && bg) return String(a.device_name || "").localeCompare(String(b.device_name || ""));
  if (ag) return -1;
  if (bg) return 1;
  return String(a.device_name || "").localeCompare(String(b.device_name || ""));
}

export function buildSelectOptions(user_access, filterOutEmptyGroupsFn) {
  if (!user_access) return [];

  const groupsRaw = Array.isArray(user_access?.groups) ? user_access.groups : [];
  let groups = groupsRaw;
  try {
    groups = filterOutEmptyGroupsFn ? filterOutEmptyGroupsFn(groupsRaw) : groupsRaw;
  } catch {
    groups = groupsRaw;
  }

  const devices = Array.isArray(user_access?.list) ? user_access.list : [];
  const out = [];

  for (const g of groups) {
    out.push({
      id: `_group_${g.group_id}`,
      device_name: g.group_name,
      group_name: "groups",
    });
  }

  const allowed = Array.isArray(user_access?.device_ids) ? new Set(user_access.device_ids) : null;
  for (const d of devices) {
    if (allowed && !allowed.has(d.id)) continue;
    out.push({
      id: d.id,
      device_name: d.device_name,
      group_name: "devices",
    });
  }

  out.sort(sortDeviceGroupList);
  return out;
}

// -------------------- PLAYLIST -> SCHEDULES --------------------
export function mapPlaylistResponseToSchedules(result, cleanFilenameFn) {
  const rows = Array.isArray(result?.list) ? result.list : [];
  return rows
    .map((row) => {
      const le = row?.library_element;
      if (!le) return null;

      const ics = String(row?.cal_ics || "").trim();
      if (!ics) return null;

      const playlistId = String(row?.playlist_id ?? "");
      if (!playlistId) return null;

      const contentId = String(le.content_id ?? "");
      const title = cleanFilenameFn
        ? cleanFilenameFn(le.filename || le.title || "scheduled")
        : le.filename || le.title || "scheduled";

      return {
        id: playlistId,
        playlistId,
        title,
        contentId,
        contentType: le.content_category || "other",
        thumbnail: le.thumbnail || null,
        ics,
        device_id: row.device_id,
        channel: row.channel,
        rank: Number(row.rank ?? 0),
      };
    })
    .filter(Boolean);
}